//src/components/dashboard/DashboardProperties.js
import { useCallback, useEffect, useMemo, useState } from 'react';
import { MdAdd, MdHomeWork, MdOutlineCurrencyExchange, MdOutlinePendingActions, MdVerified } from 'react-icons/md';
import { Link } from 'react-router-dom';
import axiosInstance from '../../axios';
import '../../styles/dashboard-properties.css';
import { formatNilaiListing, formatNilaiListingLengkap } from '../../utils/currency';
import { formatNumber, safeNumber } from '../../utils/number';
import DashboardPropertiesSkeleton from './DashboardPropertiesSkeleton';
import DashboardPropertyCard from './DashboardPropertyCard';
import DashboardPropertyFilter from './DashboardPropertyFilter';

import { MdArrowForward, MdInfoOutline, MdWarningAmber, MdWorkspacePremium } from 'react-icons/md';
import { useUserProfile } from '../../hooks/useUserProfile';
import { getSubscriptionUpgradePrompt } from '../../utils/profile';

const EMPTY_META = {
    current_page: 1,
    last_page: 1,
    per_page: 12,
    total: 0,
};

const DashboardProperties = () => {
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState('');
    const [properties, setProperties] = useState([]);
    const [meta, setMeta] = useState(EMPTY_META);
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [approvalStatusFilter, setApprovalStatusFilter] = useState('all');

    const { profile } = useUserProfile({ enabled: true });

    const subscriptionPrompt = useMemo(() => {
        return getSubscriptionUpgradePrompt(profile);
    }, [profile]);

    const fetchProperties = useCallback(async (currentPage = 1, isManualRefresh = false) => {
        if (isManualRefresh) {
            setRefreshing(true);
        } else {
            setLoading(true);
        }

        setError('');

        try {
            const response = await axiosInstance.get('/properties', {
                params: {
                    page: currentPage,
                    per_page: 12,
                },
            });

            if (response.data?.success) {
                const rawData = response.data.data;
                const list = Array.isArray(rawData) ? rawData : rawData?.data || [];
                const nextMeta = {
                    ...EMPTY_META,
                    ...(response.data.meta || {}),
                    ...(rawData?.meta || {}),
                };

                setProperties(list);
                setMeta(nextMeta);
            } else {
                setError(response.data?.message || 'Gagal memuat data properti.');
            }
        } catch (err) {
            setError(err?.response?.data?.message || 'Terjadi kesalahan saat memuat data properti.');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, []);

    useEffect(() => {
        fetchProperties(page);
    }, [page, fetchProperties]);

    const filteredProperties = useMemo(() => {
        const q = search.trim().toLowerCase();

        return properties.filter((property) => {
            const matchesSearch =
                !q ||
                property?.title?.toLowerCase().includes(q) ||
                property?.location?.toLowerCase().includes(q) ||
                property?.city?.toLowerCase().includes(q) ||
                property?.owner?.name?.toLowerCase().includes(q) ||
                property?.property_type?.name?.toLowerCase().includes(q) ||
                property?.type?.toLowerCase().includes(q);

            const propertyStatus = String(property?.status || '').toLowerCase();
            const propertyApproval = String(property?.approval_status || '').toLowerCase();

            const matchesStatus = statusFilter === 'all' || propertyStatus === statusFilter;

            const matchesApproval = approvalStatusFilter === 'all' || propertyApproval === approvalStatusFilter;

            return matchesSearch && matchesStatus && matchesApproval;
        });
    }, [properties, search, statusFilter, approvalStatusFilter]);

    const stats = useMemo(() => {
        const total = safeNumber(meta?.total) || properties.length;

        const waitingApproval = properties.filter(
            (property) => String(property?.approval_status || '').toLowerCase() === 'pending'
        ).length;

        const approved = properties.filter(
            (property) => String(property?.approval_status || '').toLowerCase() === 'approved'
        ).length;

        const totalValue = properties.reduce((sum, property) => {
            return sum + safeNumber(property?.price);
        }, 0);

        return {
            total,
            waitingApproval,
            approved,
            totalValue,
        };
    }, [meta?.total, properties]);

    const resolvedCurrentPage = Math.max(1, safeNumber(meta?.current_page) || page);

    const resolvedPerPage = Math.max(1, safeNumber(meta?.per_page) || 12);

    const resolvedTotal = safeNumber(meta?.total) || properties.length || 0;

    const resolvedLastPage = Math.max(1, safeNumber(meta?.last_page) || Math.ceil(resolvedTotal / resolvedPerPage));

    const handlePrevPage = () => {
        setPage((prev) => Math.max(prev - 1, 1));
    };

    const handleNextPage = () => {
        setPage((prev) => Math.min(prev + 1, resolvedLastPage));
    };

    return (
        <section className="dashboard-page">
            <div className="dashboard-page__header">
                <div>
                    <h1 className="dashboard-page__title">Properti Saya</h1>
                    <p className="dashboard-page__subtitle">Kelola data properti Anda</p>
                </div>

                <div className="dashboard-page__actions">
                    <Link to="/dashboard/properties/create" className="dashboard-btn dashboard-btn--primary">
                        <MdAdd size={18} aria-hidden="true" />
                        Tambah Properti
                    </Link>
                </div>
            </div>

            {subscriptionPrompt?.show && (
                <div
                    className={`dashboard-subscription-banner dashboard-subscription-banner--${subscriptionPrompt.tone}`}
                    role="alert"
                    aria-live="polite"
                >
                    <div className="dashboard-subscription-banner__content">
                        <div className="dashboard-subscription-banner__icon">
                            {subscriptionPrompt.icon === 'danger' ? (
                                <MdWarningAmber size={22} />
                            ) : subscriptionPrompt.icon === 'warning' ? (
                                <MdWarningAmber size={22} />
                            ) : subscriptionPrompt.icon === 'success' ? (
                                <MdWorkspacePremium size={22} />
                            ) : (
                                <MdInfoOutline size={22} />
                            )}
                        </div>

                        <div className="dashboard-subscription-banner__text">
                            <h3 className="dashboard-subscription-banner__title">{subscriptionPrompt.title}</h3>

                            <p className="dashboard-subscription-banner__description">
                                {subscriptionPrompt.description}
                            </p>

                            <div className="dashboard-subscription-banner__meta">
                                <span className="dashboard-subscription-banner__meta-item">
                                    Paket: {subscriptionPrompt.planLabel || '-'}
                                </span>

                                <span className="dashboard-subscription-banner__meta-item">
                                    Status: {subscriptionPrompt.planStatus || '-'}
                                </span>

                                <span className="dashboard-subscription-banner__meta-item">
                                    Kuota: {subscriptionPrompt.quotaLabel || '-'}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="dashboard-subscription-banner__actions">
                        <Link
                            to={subscriptionPrompt.ctaTo}
                            className="dashboard-btn dashboard-btn--primary dashboard-subscription-banner__cta"
                        >
                            {subscriptionPrompt.ctaLabel}
                            <MdArrowForward size={18} />
                        </Link>
                    </div>
                </div>
            )}

            <div className="dashboard-stats">
                <div className="dashboard-stat-card">
                    <div className="dashboard-stat-card__icon">
                        <MdHomeWork size={22} />
                    </div>
                    <div className="dashboard-stat-card__text">
                        <div className="dashboard-stat-card__label">Total Properti</div>
                        <div className="dashboard-stat-card__value">{formatNumber(stats.total)}</div>
                    </div>
                </div>

                <div className="dashboard-stat-card">
                    <div className="dashboard-stat-card__icon">
                        <MdOutlinePendingActions size={22} />
                    </div>
                    <div className="dashboard-stat-card__text">
                        <div className="dashboard-stat-card__label">Menunggu Persetujuan</div>
                        <div className="dashboard-stat-card__value">{formatNumber(stats.waitingApproval)}</div>
                    </div>
                </div>

                <div className="dashboard-stat-card">
                    <div className="dashboard-stat-card__icon">
                        <MdVerified size={22} />
                    </div>
                    <div className="dashboard-stat-card__text">
                        <div className="dashboard-stat-card__label">Disetujui</div>
                        <div className="dashboard-stat-card__value">{formatNumber(stats.approved)}</div>
                    </div>
                </div>

                <div className="dashboard-stat-card">
                    <div className="dashboard-stat-card__icon">
                        <MdOutlineCurrencyExchange size={22} />
                    </div>
                    <div className="dashboard-stat-card__text">
                        <div className="dashboard-stat-card__label">Total Nilai Listing</div>
                        <div
                            className="dashboard-stat-card__value dashboard-stat-card__value--money"
                            title={formatNilaiListingLengkap(stats.totalValue)}
                        >
                            {formatNilaiListing(stats.totalValue)}
                        </div>
                    </div>
                </div>
            </div>

            <DashboardPropertyFilter
                search={search}
                onSearchChange={setSearch}
                statusFilter={statusFilter}
                onStatusFilterChange={setStatusFilter}
                approvalStatusFilter={approvalStatusFilter}
                onApprovalStatusChange={setApprovalStatusFilter}
                onRefresh={() => fetchProperties(page, true)}
                refreshing={refreshing}
                searchPlaceholder="Cari judul, lokasi, pemilik, tipe, atau kota..."
            />

            {loading ? (
                <DashboardPropertiesSkeleton />
            ) : error ? (
                <div className="dashboard-alert dashboard-alert--error">{error}</div>
            ) : filteredProperties.length === 0 ? (
                <div className="dashboard-state-card">
                    <h3>Belum ada properti</h3>
                    <p>Data tidak ditemukan. Coba ubah kata kunci pencarian atau filter yang dipilih.</p>
                </div>
            ) : (
                <>
                    <div className="property-grid">
                        {filteredProperties.map((property) => (
                            <DashboardPropertyCard key={property.id} property={property} />
                        ))}
                    </div>

                    <div className="dashboard-pagination">
                        <div className="dashboard-pagination__info">
                            Halaman {resolvedCurrentPage} dari {resolvedLastPage} • {formatNumber(resolvedTotal)} total
                        </div>

                        <div className="dashboard-pagination__actions">
                            <button
                                type="button"
                                onClick={handlePrevPage}
                                disabled={resolvedCurrentPage <= 1}
                                className="dashboard-btn dashboard-btn--secondary"
                            >
                                Sebelumnya
                            </button>

                            <button type="button" disabled className="dashboard-pagination__page is-active">
                                {resolvedCurrentPage}
                            </button>

                            <button
                                type="button"
                                onClick={handleNextPage}
                                disabled={resolvedCurrentPage >= resolvedLastPage}
                                className="dashboard-btn dashboard-btn--secondary"
                            >
                                Berikutnya
                            </button>
                        </div>
                    </div>
                </>
            )}
        </section>
    );
};

export default DashboardProperties;
